<template>
  <CardModule :title="$t('registrations.producers.registration-info')">
    <v-row>
      <v-col cols="12" sm="6">
        <v-radio-group
          v-model="$loja_gerenciar.cadastro.dados_cadastrais.pessoa"
        >
          <template v-slot:label>
            <span>{{ $t("registrations.producers.type-of-person") }}:</span>
          </template>
          <v-radio
            :value="'JURIDICA'"
            class="rounded-lg pa-2"
            style="border: 1px solid rgb(0 0 0 / 42%)"
            active-class="active-class"
          >
            <template v-slot:label>
              {{ $t("registrations.producers.legal-person") }}
            </template>
          </v-radio>
          <v-radio
            :value="'FISICA'"
            class="rounded-lg pa-2"
            style="border: 1px solid rgb(0 0 0 / 42%)"
            active-class="active-class"
          >
            <template v-slot:label>
              {{ $t("registrations.producers.natural-person") }}
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <template>
      <v-expand-transition>
        <div v-show="isLegal">
          <v-row class="mt-2">
            <v-col cols="12" sm="6">
              <BaseTextField
                :label="$t('registrations.producers.social-reason')"
                :placeholder="
                  $t('registrations.producers.insert-social-reason')
                "
                v-model="$loja_gerenciar.cadastro.dados_cadastrais.razao_social"
                counter="50"
                required
                :rules="isLegal ? [rules.required, rules.max(50)] : []"
              >
              </BaseTextField>
            </v-col>
            <v-col cols="12" sm="6">
              <BaseTextField
                :placeholder="$t('registrations.producers.insert-cnpj')"
                label="CNPJ"
                required
                :rules="isLegal ? [rules.required, rules.cnpj] : []"
                v-model="$loja_gerenciar.cadastro.dados_cadastrais.cnpj"
                v-mask="'##.###.###/####-##'"
              >
              </BaseTextField>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
      <v-row v-show="!isLegal">
        <v-col cols="12" sm="6">
          <BaseTextField
            :label="$t('registrations.producers.store-name')"
            :placeholder="$t('registrations.producers.insert-store-name')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.nome_loja"
            counter="150"
            :rules="[rules.required, rules.max(150)]"
            :error-messages="
              $loja_gerenciar.cadastro.dados_cadastrais.error.nome_loja
            "
            @input="
              $loja_gerenciar.cadastro.dados_cadastrais.error.nome_loja = []
            "
          >
          </BaseTextField>
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" sm="3">
          <BaseTextField
            :placeholder="$t('registrations.producers.insert-cep')"
            :label="$t('registrations.producers.zip-code')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.cep"
            v-mask="'#####-###'"
            :rules="[rules.required, rules.min(8)]"
          >
          </BaseTextField>
        </v-col>
        <v-col cols="12" sm="3">
          <BaseAutocomplete
            :items="$config.paises.result"
            :placeholder="$t('registrations.producers.select-country')"
            :label="$t('registrations.producers.country')"
            :no-data-text="$t('registrations.no-results-found')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.pais"
            :loading="$config.loading_paises"
            item-text="dsc_lang"
            item-value="iso_full"
            needSelection
            :filter="search"
            :rules="[rules.required]"
          >
            <template v-slot:selection="{ item }">
              <template v-if="item ? (paises = item) : null">
                {{ item.dsc_lang }}
              </template>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content v-if="item">
                <v-list-item-title v-text="item.dsc_lang"></v-list-item-title>
              </v-list-item-content>
            </template>
          </BaseAutocomplete>
        </v-col>
        <v-col cols="12" sm="3">
          <BaseAutocomplete
            :items="$config.estados.result"
            :label="$t('registrations.producers.state')"
            :placeholder="$t('registrations.producers.select-state')"
            :no-data-text="$t('registrations.no-results-found')"
            required
            needSelection
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.estado"
            :loading="$config.loading_estados"
            item-text="dsc_lang"
            item-value="iso_full"
            :filter="search"
            :rules="[rules.required]"
          >
            <template v-slot:selection="{ item }">
              <template v-if="item ? (estados = item) : null">
                {{ item.dsc_lang }}
              </template>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content v-if="item">
                <v-list-item-title v-text="item.dsc_lang"></v-list-item-title>
              </v-list-item-content>
            </template>
          </BaseAutocomplete>
        </v-col>
        <v-col cols="12" sm="3">
          <BaseAutocomplete
            :label="$t('registrations.producers.city')"
            :placeholder="$t('registrations.producers.select-city')"
            :no-data-text="$t('registrations.no-results-found')"
            required
            :items="$config.cidades.result"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.cidade"
            :loading="$config.loading_cidades"
            item-text="dsc_lang"
            item-value="iso_full"
            :filter="search"
            :rules="[rules.required]"
          >
          </BaseAutocomplete>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12" sm="4">
          <BaseTextField
            label="CEP"
            :placeholder="$t('registrations.producers.select-cep')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.cep"
            type="tel"
            v-mask="'#####-###'"
            :rules="[rules.required, rules.min(8)]"
          >
          </BaseTextField>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseAutocomplete
            :items="$config.paises.result"
            :label="$t('registrations.producers.country')"
            :placeholder="$t('registrations.producers.select-country')"
            :no-data-text="$t('registrations.no-results-found')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.pais"
            :loading="$config.loading_paises"
            item-text="dsc_lang"
            item-value="iso_full"
            needSelection
            :filter="search"
            :rules="[rules.required]"
          >
            <template v-slot:selection="{ item }">
              <template v-if="item ? (paises = item) : null">
                {{ item.dsc_lang }}
              </template>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content v-if="item">
                <v-list-item-title v-text="item.dsc_lang"></v-list-item-title>
              </v-list-item-content>
            </template>
          </BaseAutocomplete>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseAutocomplete
            :items="$config.estados.result"
            :label="$t('registrations.producers.state')"
            :placeholder="$t('registrations.producers.select-state')"
            :no-data-text="$t('registrations.no-results-found')"
            required
            needSelection
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.estado"
            :loading="$config.loading_estados"
            item-text="dsc_lang"
            item-value="iso_full"
            :filter="search"
            :rules="[rules.required]"
          >
            <template v-slot:selection="{ item }">
              <template v-if="item ? (estados = item) : null">
                {{ item.dsc_lang }}
              </template>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content v-if="item">
                <v-list-item-title v-text="item.dsc_lang"></v-list-item-title>
              </v-list-item-content>
            </template>
          </BaseAutocomplete>
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" sm="4">
          <BaseTextField
            :label="$t('registrations.producers.district')"
            :placeholder="$t('registrations.producers.insert-district')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.bairro"
            type="text"
            :rules="[rules.required]"
          >
          </BaseTextField>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseTextField
            :label="$t('registrations.producers.address')"
            :placeholder="$t('registrations.producers.insert-address')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.logradouro"
            type="text"
            :rules="[rules.required]"
          >
          </BaseTextField>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseTextField
            :label="$t('registrations.producers.complement')"
            :placeholder="$t('registrations.producers.insert-complement')"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.complemento"
            type="text"
          ></BaseTextField>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12" sm="4">
          <BaseAutocomplete
            :label="$t('registrations.producers.city')"
            :placeholder="$t('registrations.producers.select-city')"
            :no-data-text="$t('registrations.no-results-found')"
            :items="$config.cidades.result"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.cidade"
            :loading="$config.loading_cidades"
            item-text="dsc_lang"
            item-value="iso_full"
            required
            :filter="search"
            :rules="[rules.required]"
          >
          </BaseAutocomplete>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseTextField
            :label="$t('registrations.producers.district')"
            :placeholder="$t('registrations.producers.insert-district')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.bairro"
            type="text"
            :rules="[rules.required]"
          >
          </BaseTextField>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseTextField
            :label="$t('registrations.producers.address')"
            :placeholder="$t('registrations.producers.insert-address')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.logradouro"
            type="text"
            :rules="[rules.required]"
          >
          </BaseTextField>
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" sm="4">
          <BaseSelect
            :label="$t('registrations.producers.segment')"
            :placeholder="$t('registrations.producers.select-segment')"
            required
            :items="filteredSegmentos"
            item-text="descricao"
            item-value="idsegmento"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.segmento"
            :color="$config.dados.corinputs"
            :item-color="$config.dados.corinputs"
            :rules="[rules.required]"
          >
          </BaseSelect>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseSelect
            label="Regional"
            :placeholder="$t('registrations.producers.select-regional')"
            required
            :items="$loja_gerenciar.regionais"
            item-text="nome"
            item-value="idregional"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.regional"
            :color="$config.dados.corinputs"
            :item-color="$config.dados.corinputs"
            :rules="[rules.required]"
          >
          </BaseSelect>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseSelect
            label="Status"
            :placeholder="$t('registrations.users.select-status')"
            required
            :items="status"
            item-text="text"
            item-value="value"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.status"
            :color="$config.dados.corinputs"
            :item-color="$config.dados.corinputs"
            :rules="[rules.required]"
          ></BaseSelect>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12" sm="4">
          <BaseTextField
            :label="$t('registrations.producers.insert-complement')"
            :placeholder="$t('registrations.producers.insert-complement')"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.complemento"
            type="text"
          ></BaseTextField>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseSelect
            :label="$t('registrations.producers.segment')"
            :placeholder="$t('registrations.producers.select-segment')"
            :items="filteredSegmentos"
            required
            item-text="descricao"
            item-value="idsegmento"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.segmento"
            :color="$config.dados.corinputs"
            :item-color="$config.dados.corinputs"
            :rules="[rules.required]"
          >
          </BaseSelect>
        </v-col>
        <v-col cols="12" sm="4">
          <BaseSelect
            label="Regional"
            :placeholder="$t('registrations.producers.select-regional')"
            required
            :items="$loja_gerenciar.regionais"
            item-text="nome"
            item-value="idregional"
            class="textinput"
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.regional"
            :color="$config.dados.corinputs"
            :item-color="$config.dados.corinputs"
            :rules="[rules.required]"
          >
          </BaseSelect>
        </v-col>
      </v-row>
      <v-row v-show="isLegal">
        <v-col cols="12" sm="6">
          <BaseTextField
            :label="$t('registrations.producers.store-name')"
            :placeholder="$t('registrations.producers.insert-store-name')"
            required
            v-model="$loja_gerenciar.cadastro.dados_cadastrais.nome_loja"
            type="text"
            counter="150"
            :rules="isLegal ? [rules.required, rules.max(150)] : []"
          >
          </BaseTextField>
        </v-col>
      </v-row>
    </template>
    <h1 class="lojas-gerenciar-dados-cadastrais-subtitle mt-6">
      {{
        isLegal
          ? $t("registrations.producers.responsible-person-info")
          : $t("registrations.producers.representative-info")
      }}
    </h1>
    <v-divider></v-divider>
    <v-row class="mt-5">
      <v-col cols="12" sm="6">
        <BaseTextField
          required
          :label="
            isLegal
              ? $t('registrations.producers.representative-name')
              : $t('registrations.producers.responsible-person-name')
          "
          :placeholder="
            isLegal
              ? $t('registrations.producers.insert-representative-name')
              : $t('registrations.producers.insert-responsible-person-name')
          "
          v-model="$loja_gerenciar.cadastro.dados_cadastrais.nome"
          type="text"
          :rules="[rules.required]"
        >
        </BaseTextField>
      </v-col>
      <v-col cols="12" sm="6">
        <BaseTextField
          label="E-mail"
          :placeholder="$t('registrations.users.email-example')"
          required
          v-model="$loja_gerenciar.cadastro.dados_cadastrais.email"
          type="text"
          :rules="[rules.required, rules.email]"
        >
        </BaseTextField>
      </v-col>
    </v-row>
    <v-row class="mb-2">
      <v-col cols="12" sm="4">
        <BaseTextField
          label="CPF"
          :placeholder="$t('registrations.producers.insert-cpf')"
          required
          v-model="$loja_gerenciar.cadastro.dados_cadastrais.cpf"
          type="tel"
          v-mask="'###.###.###-##'"
          :rules="[rules.required, rules.cpf]"
        >
        </BaseTextField>
      </v-col>
      <v-col cols="12" sm="4">
        <BaseTextField
          label="RG"
          :placeholder="$t('registrations.producers.insert-rg')"
          required
          v-model="$loja_gerenciar.cadastro.dados_cadastrais.rg"
          type="text"
          :rules="[rules.required]"
        >
        </BaseTextField>
      </v-col>
      <v-col cols="12" sm="4">
        <BaseTextField
          :label="$t('registrations.producers.phone')"
          :placeholder="$t('registrations.producers.insert-phone')"
          v-model="$loja_gerenciar.cadastro.dados_cadastrais.celular"
          required
          type="text"
          v-mask="['(##) ####-####', '(##) #####-####']"
          :rules="[rules.required, rules.cellphone]"
        >
        </BaseTextField>
      </v-col>
    </v-row>
  </CardModule>
</template>
<script>
import LojasGerenciarMixins from "@/mixins/Loja-Gerenciar.mixins.js";
import formRules from "../../mixins/formRules";
export default {
  name: "RegistrationData",
  mixins: [LojasGerenciarMixins, formRules],
  components: {
    CardModule: () => import("@/components/cards/CardModule.vue"),
    BaseTextField: () => import("@/components/forms/BaseTextField.vue"),
    BaseAutocomplete: () => import("@/components/forms/BaseAutocomplete.vue"),
    BaseSelect: () => import("@/components/forms/BaseSelect.vue"),
  },
  data() {
    return {
      paises: [],
      estados: [],
      cidades: [],
    };
  },
  computed: {
    // eslint-disable-next-line vue/return-in-computed-property
    filteredSegmentos() {
      if (this.$loja_segmentos.segmentos) {
        return this.$loja_segmentos.segmentos.filter(
          segmento => segmento.ativo === 1
        );
      }
    },
    isLegal() {
      return (
        this.$loja_gerenciar.cadastro.dados_cadastrais.pessoa == "JURIDICA"
      );
    },
  },
  methods: {
    replaceSpecialChars(str) {
      str = str.toLowerCase();
      str = str.replace(/[ÀÁÂÃÄÅ]/, "a");
      str = str.replace(/[àáâãäå]/, "a");
      str = str.replace(/[ÈÉÊË]/, "e");
      str = str.replace(/[èèêë]/, "e");
      str = str.replace(/[Ç]/, "c");
      str = str.replace(/[ç]/, "c");
      str = str
        .toString()
        .normalize("NFD")
        .replace(/[^a-zA-Zs]/g, "");
      str = str.replace(" ", "-");

      return str;
    },
    search(array, search) {
      let pesquisa = this.replaceSpecialChars(
        search.toString().toLocaleLowerCase().normalize("NFD")
      );
      let palavra = this.replaceSpecialChars(
        array.dsc_lang.toString().toLocaleLowerCase().normalize("NFD")
      );

      if (palavra.indexOf(pesquisa) != -1) {
        return array;
      }
    },
  },
  watch: {
    paises(e) {
      this.$config.getEstados(e.id);
    },
    estados(e) {
      this.$config.getCidades(e.id);
    },
    "$loja_gerenciar.cadastro.dados_cadastrais.nome_loja"(e) {
      if (
        this.$loja_gerenciar.cadastro.dados_cadastrais.tipo_slug == "DEFAULT"
      ) {
        this.$loja_gerenciar.cadastro.dados_cadastrais.slug_loja =
          this.replaceSpecialChars(e);
      }
    },
    "$loja_gerenciar.cadastro.dados_cadastrais.tipo_slug"(e) {
      if (e == "DEFAULT") {
        this.$loja_gerenciar.cadastro.dados_cadastrais.slug_loja =
          this.replaceSpecialChars(
            this.$loja_gerenciar.cadastro.dados_cadastrais.nome_loja
          );
      }
    },
    "$loja_gerenciar.cadastro.dados_cadastrais.cep"(e) {
      if (e.length === 9) {
        this.$axios
          .get(`https://viacep.com.br/ws/${e}/json/`)
          .then(res => {
            if (res.erro) {
              throw new Error();
            }
            this.$loja_gerenciar.cadastro.dados_cadastrais.pais = "Brasil";
            this.$loja_gerenciar.cadastro.dados_cadastrais.estado = res.uf
              ? res.uf
              : this.$loja_gerenciar.cadastro.dados_cadastrais.estado;
            this.$loja_gerenciar.cadastro.dados_cadastrais.cidade =
              res.localidade
                ? res.localidade
                : this.$loja_gerenciar.cadastro.dados_cadastrais.cidade;
            this.$loja_gerenciar.cadastro.dados_cadastrais.bairro = res.bairro
              ? res.bairro
              : this.$loja_gerenciar.cadastro.dados_cadastrais.bairro;
            this.$loja_gerenciar.cadastro.dados_cadastrais.logradouro =
              res.logradouro
                ? res.logradouro
                : this.$loja_gerenciar.cadastro.dados_cadastrais.logradouro;
          })
          .catch(error => {
            console.error(error);
          });
      }
    },
  },
  mounted() {
    if (this.$loja_gerenciar.lojaAddOrEdit == "new") {
      this.$loja_gerenciar.cadastro.dados_cadastrais.pessoa = "JURIDICA";
    }
    this.$config.getPaises();
  },
};
</script>
<style scoped>
.active-class {
  background-color: #e6f7ff !important;
  border-color: #91d5ff !important;
  color: #1890ff !important;
}
.lojas-gerenciar-dados-cadastrais-subtitle {
  width: 487px;
  font-size: 1rem;
  align-self: flex-start;
  margin-top: 1rem;
}
</style>
